import { Mail, Linkedin, ExternalLink } from "lucide-react";
import { NeuralGlobeLogo } from "@/components/ui/logo";

interface FooterProps {
  language: 'en' | 'de';
}

export const Footer = ({ language }: FooterProps) => {
  const content = {
    en: {
      tagline: "Executive Consulting",
      contact: "Get in Touch",
      copyright: "© 2026 Stefan Kaiser. All rights reserved.",
      expertise: "Expertise",
      industries: ["Automotive", "IT", "SAP", "Manufacturing", "Software", "AI", "Development", "Business Models", "Building", "Telco", "Public Sector", "Startups"]
    },
    de: {
      tagline: "Executive Consulting",
      contact: "Kontakt aufnehmen",
      copyright: "© 2026 Stefan Kaiser. Alle Rechte vorbehalten.",
      expertise: "Expertise",
      industries: ["Automotive", "IT", "SAP", "Fertigung", "Software", "KI", "Entwicklung", "Geschäftsmodelle", "Bau", "Telekommunikation", "Öffentlicher Sektor", "Startups"]
    }
  };

  const t = content[language];

  return (
    <footer id="get-in-touch" className="bg-primary text-primary-foreground py-12">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid md:grid-cols-3 gap-8 mb-8">
          {/* Brand */}
          <div>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg gradient-accent flex items-center justify-center text-accent-foreground shadow-glow">
                <NeuralGlobeLogo className="w-6 h-6" />
              </div>
              <div>
                <div className="font-semibold">Stefan Kaiser</div>
                <div className="text-sm text-primary-foreground/80">{t.tagline}</div>
              </div>
            </div>
            <div className="text-sm text-primary-foreground/70 space-y-1">
              <div className="flex items-center gap-2">
                <svg className="w-5 h-4 rounded-sm overflow-hidden flex-shrink-0" viewBox="0 0 32 24">
                  <rect width="32" height="24" fill="#DA291C"/>
                  <rect x="13" y="6" width="6" height="12" fill="white"/>
                  <rect x="10" y="9" width="12" height="6" fill="white"/>
                </svg>
                <span>Switzerland</span>
              </div>
              <div className="flex items-center gap-2">
                <svg className="w-5 h-4 rounded-sm overflow-hidden flex-shrink-0" viewBox="0 0 32 24">
                  <rect width="32" height="8" fill="#000"/>
                  <rect y="8" width="32" height="8" fill="#DD0000"/>
                  <rect y="16" width="32" height="8" fill="#FFCC00"/>
                </svg>
                <span>Germany</span>
              </div>
              <div className="flex items-center gap-2">
                <svg className="w-5 h-4 rounded-sm overflow-hidden flex-shrink-0" viewBox="0 0 32 24">
                  <rect width="32" height="8" fill="#ED2939"/>
                  <rect y="8" width="32" height="8" fill="white"/>
                  <rect y="16" width="32" height="8" fill="#ED2939"/>
                </svg>
                <span>Austria</span>
              </div>
              <div className="flex items-center gap-2">
                <svg className="w-5 h-4 rounded-sm overflow-hidden flex-shrink-0" viewBox="0 0 32 24">
                  <rect width="32" height="12" fill="#002B7F"/>
                  <rect y="12" width="32" height="12" fill="#CE1126"/>
                </svg>
                <span>Liechtenstein</span>
              </div>
              <div className="flex items-center gap-2">
                <svg className="w-5 h-4 rounded-sm overflow-hidden flex-shrink-0" viewBox="0 0 32 24">
                  <rect width="32" height="8" fill="#FDB913"/>
                  <rect y="8" width="32" height="8" fill="#006A44"/>
                  <rect y="16" width="32" height="8" fill="#C1272D"/>
                </svg>
                <span>Lithuania</span>
              </div>
            </div>
          </div>

          {/* Expertise */}
          <div>
            <h3 className="font-semibold mb-3">{t.expertise}</h3>
            <div className="flex flex-wrap gap-2">
              {t.industries.map((industry, index) => (
                <span 
                  key={index}
                  className="text-sm bg-primary-foreground/10 px-3 py-1 rounded-full border border-primary-foreground/20"
                >
                  {industry}
                </span>
              ))}
            </div>
          </div>

          {/* Contact */}
          <div>
            <h3 className="font-semibold mb-3">{t.contact}</h3>
            <div className="flex flex-col gap-2">
              <a 
                href="mailto:sk@stefankaiser.net" 
                className="flex items-center gap-2 text-primary-foreground/90 hover:text-accent transition-smooth"
              >
                <Mail className="h-4 w-4" />
                <span className="text-sm">sk@stefankaiser.net</span>
              </a>
              <a 
                href="https://www.linkedin.com/in/kaiserstefan/"
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-primary-foreground/90 hover:text-accent transition-smooth"
              >
                <Linkedin className="h-4 w-4" />
                <span className="text-sm">LinkedIn</span>
              </a>
              <a 
                href="https://baltx.com"
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-primary-foreground/90 hover:text-accent transition-smooth"
              >
                <ExternalLink className="h-4 w-4" />
                <span className="text-sm">baltx.com</span>
              </a>
            </div>
          </div>
        </div>

        <div className="border-t border-primary-foreground/20 pt-6 text-center">
          <p className="text-sm text-primary-foreground/70">{t.copyright}</p>
        </div>
      </div>
    </footer>
  );
};